import React, {useState} from "react";
import Button from "../Button";
import TriggerEditor from "../TriggerEditor";
import {agent} from "../../api/agent";
import {ConditionalVariableBranch, ConditionalVariableElement, VariableField} from "./PipelineConfig";

interface Props {
    element: ConditionalVariableElement;
    onChange: () => void;
}

export default function ConditionalVariablesEditor({element, onChange}: Props) {
    const addBranch = () => {
        element.branches.push({
            id: crypto.randomUUID(),
            condition: {},
            variables: [],
        });
        onChange();
    };

    const removeBranch = (branch: ConditionalVariableBranch) => {
        const index = element.branches.indexOf(branch);
        if (index !== -1) {
            element.branches.splice(index, 1);
            onChange();
        }
    };

    return (
        <div style={{display: 'flex', flexDirection: 'column', gap: '1rem'}}>
            {element.branches.map((branch, index) => (
                <div key={branch.id} style={{
                    border: '1px solid var(--content-box-border)',
                    borderRadius: 8,
                    padding: '0.75rem'
                }}>
                    <div style={{display: 'flex', justifyContent: 'space-between', alignItems: 'center'}}>
                        <strong>{index === 0 ? 'If' : 'Else if'}</strong>
                        <Button style={{color: 'red'}} onClick={() => removeBranch(branch)}>Remove branch</Button>
                    </div>
                    <TriggerEditor
                        title="Condition"
                        value={branch.condition}
                        onChange={(newTrigger) => {
                            branch.condition = newTrigger ?? {};
                            onChange();
                        }}
                    />
                    <div style={{marginTop: '0.5rem'}}>
                        <label>Then set variables:</label>
                        <VariableRowsEditor
                            rows={branch.variables}
                            onChange={(rows) => {
                                branch.variables = rows;
                                onChange();
                            }}
                        />
                    </div>
                </div>
            ))}
            <Button onClick={addBranch}>Add {element.branches.length === 0 ? 'If' : 'Else If'}-Condition</Button>
            <div style={{
                border: '1px solid var(--content-box-border)',
                borderRadius: 8,
                padding: '0.75rem'
            }}>
                <strong>Else</strong>
                <div style={{marginTop: '0.5rem'}}>
                    <label>Set variables:</label>
                    <VariableRowsEditor
                        rows={element.elseVariables}
                        onChange={(rows) => {
                            element.elseVariables = rows;
                            onChange();
                        }}
                    />
                </div>
            </div>
        </div>
    );
}

function VariableRowsEditor({rows, onChange}: { rows: VariableField[]; onChange: (rows: VariableField[]) => void }) {
    const [lastEmptyId, setLastEmptyId] = useState(() => crypto.randomUUID());

    const displayRows = [...rows, {key: '', value: '', id: lastEmptyId}];

    return (
        <div>
            {displayRows.map((row) => (
                <div key={row.id} style={{display: 'flex', gap: '0.5rem', marginTop: '0.5rem'}}>
                    <input
                        type="text"
                        placeholder="Key"
                        value={row.key}
                        onChange={(e) => {
                            const newRows = [...rows];
                            let variable = newRows.find((r) => r.id === row.id);
                            if (!variable && e.target.value.length > 0) {
                                variable = {key: e.target.value, value: '', id: row.id};
                                newRows.push(variable);
                                setLastEmptyId(crypto.randomUUID());
                            }
                            if (variable) {
                                variable.key = e.target.value;
                            }
                            onChange(newRows);
                        }}
                        onBlur={(e) => {
                            if (e.target.value.length === 0) {
                                onChange(rows.filter((r) => r.id !== row.id));
                            }
                        }}
                    />
                    <i className="fa-solid fa-arrow-right" style={{marginTop: '.5rem'}}></i>
                    <input
                        type="text"
                        placeholder="Value (can contain ${variables})"
                        value={row.value}
                        onChange={(e) => {
                            const newRows = [...rows];
                            let variable = newRows.find((r) => r.id === row.id);
                            if (!variable && e.target.value.length > 0) {
                                variable = {key: '', value: e.target.value, id: row.id};
                                newRows.push(variable);
                                setLastEmptyId(crypto.randomUUID());
                            }
                            if (variable) {
                                variable.value = e.target.value;
                            }
                            onChange(newRows);
                        }}
                    />
                    <div
                        style={{marginTop: '.25rem', cursor: 'pointer'}}
                        onClick={async () => {
                            if (!row.value) return;
                            const wasEncrypted = row.value.startsWith('ENC[v1]:');
                            const newValue = wasEncrypted
                                ? (await agent.decrypt(row.value)).decryptedData
                                : (await agent.encrypt(row.value)).encryptedData;

                            const newRows = [...rows];
                            let variable = newRows.find((r) => r.id === row.id);
                            if (!variable && row.key.length > 0) {
                                variable = {key: row.key, value: newValue, id: row.id};
                                newRows.push(variable);
                                setLastEmptyId(crypto.randomUUID());
                            }
                            if (variable) {
                                variable.value = newValue;
                            }
                            onChange(newRows);
                        }}>
                        {!row.value.startsWith('ENC[v1]:') && (<i className="fa-solid fa-lock"></i>)}
                        {row.value.startsWith('ENC[v1]:') && (<i className="fa-solid fa-lock-open"></i>)}
                    </div>
                </div>
            ))}
        </div>
    );
}
